import {useEffect, useState} from 'react'
import {useForm} from 'react-hook-form'
import {
    getDuplicatedItemData,
    isNew,
    useDuplicateFormId,
} from 'services/utils/editForm'
import {yupResolver} from '@hookform/resolvers/yup'
import {sessionSchema} from 'services/validations/schemas/sessions'
import {useGetCitiesQuery} from 'store/slices/api/citiesApi'
import {useGetCountriesQuery} from 'store/slices/api/countriesApi'
import {useGetGuidesQuery} from 'store/slices/api/guidesApi'
import {useGetDancesQuery} from 'store/slices/api/dancesApi'
import {
    useCreateSessionMutation,
    useGetSessionQuery,
    useSaveSessionMutation,
    useReorderLearnedDancesMutation,
    useDeleteLearnedDanceMutation,
} from 'store/slices/api/sessionsApi'
import {useI18n} from 'store/Store'
import {useAlert} from 'components/Display/Alert/AlertContext'
import {urls} from 'services/utils/urls'
import {useUploadImageMutation} from 'store/slices/api/uploadsApi'
import {useNavigate} from 'react-router-dom'

const defaultValues = {
    isActive: true,
    isWeekly: true,
    airConditioner: true,
}

const useSessionForm = (sessionId) => {
    const {i18n} = useI18n()
    const {showAlert} = useAlert()
    const navigate = useNavigate()
    const isNewSession = isNew(sessionId)
    const duplicateFromId = useDuplicateFormId(sessionId)
    const formSessionId = isNewSession ? duplicateFromId : sessionId

    const form = useForm({
        mode: 'all',
        resolver: yupResolver(sessionSchema),
        defaultValues,
    })
    const {handleSubmit} = form

    const [formError, setFormError] = useState()
    const [searchGuideValue, setSearchGuideValue] = useState()
    const [searchCountryValue, setSearchCountryValue] = useState()
    const [searchCityValue, setSearchCityValue] = useState()
    const [searchDanceValue, setSearchDanceValue] = useState('')

    const [doCreateSession] = useCreateSessionMutation()
    const [doSaveSession] = useSaveSessionMutation()
    const [doReorderLearnedDances] = useReorderLearnedDancesMutation()
    const [doDeleteLearnedDance] = useDeleteLearnedDanceMutation()
    const {data: session} = useGetSessionQuery(formSessionId, {
        skip: !formSessionId,
    })

    const {data: guides} = useGetGuidesQuery(
        {
            search: searchGuideValue,
        },
        {skip: !searchGuideValue}
    )
    const {data: countries} = useGetCountriesQuery(
        {search: searchCountryValue},
        {skip: !searchCountryValue}
    )
    const {data: cities} = useGetCitiesQuery(
        {search: searchCityValue},
        {skip: !searchCityValue}
    )

    const {data: dances, isFetching: isFetchingDances} = useGetDancesQuery(
        {search: searchDanceValue},
        {skip: !searchDanceValue}
    )

    const [doUploadImage] = useUploadImageMutation()

    useEffect(() => {
        if (session) {
            form.reset(isNewSession ? getDuplicatedItemData(session) : session)
        }
    }, [form, session, isNewSession])

    const formatBody = (values) => {
        const guides = values.guides?.map((guide) => guide._id)
        const city = values.city?._id
        const country = values.country?._id
        const learnedDances =
            values.learnedDances?.map((dance) => dance._id) || []

        return {
            ...values,
            guides,
            city,
            country,
            learnedDances,
        }
    }

    const onSubmit = async (values) => {
        let response
        const body = formatBody(values)
        if (values._id) {
            response = await doSaveSession(body)
        } else {
            response = await doCreateSession(body)
        }
        if (response?.data?._id) {
            const message = i18n.t('form.savedSuccessfully', {
                itemName: i18n.t('tables.session'),
            })
            navigate(`/sessions/${response.data._id}`)
            showAlert(message)
        }
        if (response.error) {
            setFormError(response.error.data.error)
        }
    }

    const submit = handleSubmit(onSubmit)

    const handleSetValue = (value, name) => {
        form.setValue(name, value)
    }

    const getImageUrl = () => {
        const {base, sessionsPictures} = urls
        if (session?.imageUrl) {
            // backward compatibility
            return session.imageUrl.includes(sessionsPictures)
                ? `https://rokdim.co.il/${session.imageUrl}`
                : `${base}/${sessionsPictures}/${session.imageUrl}`
        } else if (session?.hasPicture) {
            return `${base}/${sessionsPictures}/sp${session._id}`
        }
    }

    const uploadImage = async (selectedFile) => {
        const fileToUpload =
            selectedFile instanceof File
                ? selectedFile
                : form.getValues('imageUrl')

        const formData = new FormData()
        formData.append('files', fileToUpload)
        formData.append('folderName', urls.sessionsPictures)

        const response = await doUploadImage({
            formData,
            folderName: urls.sessionsPictures,
        })

        const link = response?.data?.files?.[0].link

        if (link) {
            form.setValue('imageUrl', link)
        }
    }

    const learnedDancesValues = form.watch('learnedDances') || []

    const deleteDanceFromServer = async (danceId) => {
        try {
            await doDeleteLearnedDance({
                sessionId,
                danceId,
            }).unwrap()
        } catch (error) {
            showAlert(
                error?.data?.error ||
                    i18n.t('general.error', {
                        defaultValue: 'An error occurred',
                    })
            )
            return false
        }
        return true
    }

    const handleRemoveDance = async (index) => {
        if (!learnedDancesValues.length) return

        const danceToRemove = learnedDancesValues[index]
        const updatedDances = [...learnedDancesValues]
        updatedDances.splice(index, 1)
        form.setValue('learnedDances', updatedDances, {shouldDirty: true})

        const shouldDeleteFromServer =
            !isNew(sessionId) && sessionId && danceToRemove?._id

        if (shouldDeleteFromServer) {
            const success = await deleteDanceFromServer(danceToRemove._id)
            if (!success) {
                form.setValue('learnedDances', learnedDancesValues, {
                    shouldDirty: true,
                })
            }
        }
    }

    const isInvalidMove = (index, newIndex) =>
        !learnedDancesValues.length ||
        newIndex < 0 ||
        newIndex >= learnedDancesValues.length ||
        index === newIndex

    const handleMoveDance = async (index, newIndex) => {
        if (isInvalidMove(index, newIndex)) return

        const updatedDances = [...learnedDancesValues]
        const dance = learnedDancesValues[index]
        updatedDances.splice(index, 1)
        updatedDances.splice(newIndex, 0, dance)
        form.setValue('learnedDances', updatedDances, {shouldDirty: true})

        if (!isNew(sessionId) && sessionId) {
            try {
                const danceIds = updatedDances.map((d) => d._id)
                await doReorderLearnedDances({
                    sessionId,
                    danceIds,
                }).unwrap()
            } catch (error) {
                showAlert(
                    error?.data?.error ||
                        i18n.t('general.error', {
                            defaultValue: 'An error occurred',
                        })
                )
            }
        }
    }

    const isExists = (dance) =>
        learnedDancesValues.some((item) => item._id === dance._id)

    const handleAddDance = (dance) => {
        if (!dance?._id) {
            return {added: false, reason: 'invalid'}
        }

        if (isExists(dance)) {
            return {added: false, reason: 'duplicate', dance}
        }

        const value = [...learnedDancesValues, dance]
        form.setValue('learnedDances', value, {shouldDirty: true})
        return {added: true, dance}
    }

    return {
        form,
        submit,
        session,
        formError,
        guidesResults: guides,
        searchGuideValue,
        setSearchGuideValue,
        countriesResults: countries,
        searchCountryValue,
        setSearchCountryValue,
        citiesResults: cities,
        searchCityValue,
        setSearchCityValue,
        handleSetValue,
        imageUrl: getImageUrl(),
        uploadImage,
        dancesResults: dances,
        dancesResultsLoading: isFetchingDances,
        setSearchDanceInputValue: setSearchDanceValue,
        searchDanceValue,
        handleAddDance,
        handleRemoveDance,
        handleMoveDance,
        learnedDancesValues,
    }
}

export default useSessionForm
